<template>
  <RevForm :initial-values :validate @submit="handleSubmit">
    <template #default="{ errors, values }">
      <div class="grid-standard !gap-x-16 !gap-y-12">
        <RevInputSelect
          v-if="shouldShowMarketSelect"
          :id="`market_${values.market}`"
          v-model="values.market as string"
          class="md:col-span-2 lg:col-span-6"
          :error="errors.market"
          :label="i18n(translations.addFormMarketSelectLabel)"
          :options="marketOptions"
        />

        <RevInputSelect
          :id="`carrier_${values.market}`"
          v-model="values.carrierId"
          class="md:col-span-2 lg:col-span-6"
          :error="errors.carrierId"
          :label="i18n(translations.carrier)"
          :options="carrierOptions"
        />

        <RevInputSelect
          v-if="!shouldShowMarketSelect && categoryOptions.length"
          :id="`categories_${values.market}`"
          v-model="values.categoryId"
          class="md:col-span-2 lg:col-span-6"
          :error="errors.categoryId"
          :label="i18n(translations.category)"
          :options="categoryOptions"
        />

        <div class="md:col-span-2 lg:col-span-6">
          <RevInputText
            :id="`cost_${values.market}`"
            v-model.number="values.cost"
            :error="errors.cost"
            :label="
              i18n(translations.cost, { currency: i18n.currencySign(currency) })
            "
            name="cost"
            type="number"
          />
          <p class="body-2 mt-12">
            {{ i18n(translations.addShippingFormCostDescription) }}
          </p>
        </div>

        <div
          v-if="!isBackship(values.carrierId)"
          class="md:col-span-2 lg:col-span-6"
        >
          <RevInputSelect
            :id="`delay_${values.market}`"
            v-model="values.shippingDelay"
            :error="errors.shippingDelay"
            :label="i18n(translations.shippingDelay)"
            name="shippingDelay"
            :options="shippingDelayOptions"
          />

          <p class="body-2 mt-12">
            {{ i18n(translations.shippingDelayExplanation) }}
          </p>

          <p class="body-2">
            {{ i18n(translations.shippingDateExplanation) }}
          </p>
        </div>
      </div>

      <RevButton
        class="mt-28 md:mt-24"
        full-width="adaptive"
        :loading="isSubmitting"
        type="submit"
        variant="secondary"
      >
        {{ isEditing ? i18n(translations.edit) : i18n(translations.add) }}
      </RevButton>

      <RevButton
        class="ml-0 mt-16 md:ml-16"
        full-width="adaptive"
        type="button"
        variant="secondaryDestructive"
        @click="handleCancel"
      >
        {{ i18n(translations.cancel) }}
      </RevButton>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { omit } from '@backmarket/utils/object/omit'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate } from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { CATEGORY_TYPES } from '../../ShippingLegacy.constants'
import { useMetaData } from '../../composables/useMetaData'

import translations from './Form.translations'
import type { FormValues } from './Form.types'

const { activeMarkets } = useSellerInfoStore()

defineProps<{
  initialValues: FormValues
  isEditing: boolean
  isSubmitting: boolean
  shouldShowMarketSelect: boolean
}>()
const emit = defineEmits(['submit', 'cancel'])

const i18n = useI18n()
const currency = useMarketplace().market.defaultCurrency

const { carrierOptions, categoryOptions, shippingDelayOptions } = useMetaData()

const validate = makeValidate<FormValues>({})

const marketOptions = computed(() =>
  activeMarkets.map((market) => ({
    label: i18n.country(market),
    value: market,
  })),
)

function handleSubmit(values: FormValues) {
  emit(
    'submit',
    values.categoryId === CATEGORY_TYPES.GLOBAL
      ? omit(values, ['categoryId'])
      : values,
  )
}

function handleCancel() {
  emit('cancel')
}

function isBackship(carrierId: string) {
  const carrierOption = carrierOptions.value.find(
    ({ value }) => value === carrierId,
  )

  // BackShip provides by default a delay
  // No need to ask that information if he selects a BackShip shipper
  return carrierOption?.meta?.backship || false
}
</script>
